import { z } from "zod";

// TES-863 (Ö-211, groups 3, 5 and 6) — the parts of the medical profile that
// had no structured home: they lived only inside the patient's F-02 answers.
//
// Emelie 2026-09-22, verbatim:
// "Kirill korrigerar den strukturerade, aktuella medicinska profilen genom en rättad version bredvid originalet."
//
// A correction must carry, in her list:
// "ursprungligt värde · rättat värde · ändringsorsak · tidpunkt · ansvarig · källa · auditlogg"
// The chain (original row, then each correction superseding the one before)
// carries the first two, the row carries reason, time, author and source, and
// the route writes the audit row.
//
// Each kind is ONE statement, corrected as a whole — the whole medication
// list, not one drug — so correcting a strength cannot leave a stale interval
// standing beside it. Same reason MeasurementSession is superseded per
// occasion.

export const MEDICAL_PROFILE_KINDS = [
  // Group 3 — current medicines and supplements, plus the weight/diabetes
  // medicine history F-02 asks separately.
  "medications",
  // Group 5 — allergies and hypersensitivity.
  "allergies",
  // Group 6 — pregnancy, breastfeeding and relevant reproductive status.
  "reproductive_status",
] as const;
export const medicalProfileKindSchema = z.enum(MEDICAL_PROFILE_KINDS);
export type MedicalProfileKind = z.infer<typeof medicalProfileKindSchema>;

const text = (max: number) => z.string().trim().max(max).nullable();

// The F-02 medication entry, part for part (`med_current_medications`,
// "Hälsoformulär v3" §4.5): name, strength or dose, how often, reason.
export const medicationItemSchema = z.object({
  name: z.string().trim().min(1).max(200),
  strength: text(200),
  frequency: text(200),
  reason: text(500),
});
export type MedicationItem = z.infer<typeof medicationItemSchema>;

// F-02's own option ids, so a corrected value and the declared one compare.
export const WEIGHT_MEDICINE_HISTORY_VALUES = ["nej", "ja", "osaker"] as const;
export const ALLERGY_ANSWER_VALUES = ["nej", "ja"] as const;
export const REPRODUCTIVE_STATUS_VALUES = [
  "inte_gravid",
  "gravid",
  "ammar",
  "forsoker",
  "planerar",
  "osaker",
  "vill_inte_ange",
] as const;

export const medicationsValueSchema = z.object({
  current: z.array(medicationItemSchema).max(50),
  weightMedicineHistory: z.enum(WEIGHT_MEDICINE_HISTORY_VALUES).nullable(),
  weightMedicineHistoryDetail: text(4000),
});
export const allergiesValueSchema = z.object({
  hasAllergies: z.enum(ALLERGY_ANSWER_VALUES).nullable(),
  detail: text(4000),
});
export const reproductiveStatusValueSchema = z.object({
  status: z.enum(REPRODUCTIVE_STATUS_VALUES).nullable(),
});
export type MedicationsValue = z.infer<typeof medicationsValueSchema>;
export type AllergiesValue = z.infer<typeof allergiesValueSchema>;
export type ReproductiveStatusValue = z.infer<
  typeof reproductiveStatusValueSchema
>;

export const MEDICAL_PROFILE_VALUE_SCHEMAS = {
  medications: medicationsValueSchema,
  allergies: allergiesValueSchema,
  reproductive_status: reproductiveStatusValueSchema,
} as const;

// Swedish labels for the admin, verbatim from F-02's options.
export const REPRODUCTIVE_STATUS_LABELS: Record<
  (typeof REPRODUCTIVE_STATUS_VALUES)[number],
  string
> = {
  inte_gravid: "Inte gravid",
  gravid: "Gravid",
  ammar: "Ammar",
  forsoker: "Försöker bli gravid",
  planerar: "Planerar graviditet",
  osaker: "Osäker",
  vill_inte_ange: "Vill inte ange",
};

// POST body. `supersedesEntryId` names the row being corrected; null means
// "correct what she declared", which the server resolves to the latest
// health declaration. Required, not optional: correcting and adding are
// different acts, and the server refuses a null when a correction already
// exists (the client must name the current row, so two doctors cannot both
// correct the same value unseen).
//
// The reason is free text, like the measurement correction's: Emelie wrote
// no taxonomy, and an invented one would carry less than the doctor's own
// sentence.
const correctionBase = {
  supersedesEntryId: z.string().uuid().nullable(),
  reason: z.string().trim().min(1).max(2000),
};
export const correctMedicalProfileSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("medications"),
    value: medicationsValueSchema,
    ...correctionBase,
  }),
  z.object({
    kind: z.literal("allergies"),
    value: allergiesValueSchema,
    ...correctionBase,
  }),
  z.object({
    kind: z.literal("reproductive_status"),
    value: reproductiveStatusValueSchema,
    ...correctionBase,
  }),
]);
export type CorrectMedicalProfile = z.infer<typeof correctMedicalProfileSchema>;

export type MedicalProfileValue =
  | MedicationsValue
  | AllergiesValue
  | ReproductiveStatusValue;

// One stored row as the doctor reads it.
export interface MedicalProfileEntryDto {
  id: string;
  source: "form" | "clinician";
  value: MedicalProfileValue;
  sourceSubmissionId: string | null;
  recordedBy: { id: string; name: string } | null;
  correctionReason: string | null;
  supersedesId: string | null;
  current: boolean;
  createdAt: string;
}

// One kind as the doctor reads it.
//
// `current` is what is true now: the head of the chain for her latest
// declaration, or — when nobody has corrected it — the declaration itself,
// read straight from the form (`entryId: null`). `history` is the chain,
// oldest first, so the original always stands beside its correction.
export interface MedicalProfileSectionDto {
  kind: MedicalProfileKind;
  current: {
    entryId: string | null;
    source: "form" | "clinician";
    value: MedicalProfileValue;
    submissionId: string | null;
    at: string | null;
  } | null;
  history: MedicalProfileEntryDto[];
}

export interface MedicalProfileResponse {
  sections: MedicalProfileSectionDto[];
}
